import request from "superagent";
import Auth from "../../routes/Auth";

const API = process.env.REACT_APP_API_URL;

// BE accepts "username" + ("section" | "section_id") columns. These
// aliases let instructors use natural header names on their CSV; we
// rewrite them in-place before POST. Unknown headers pass through so
// a typo surfaces in the BE error map rather than being dropped here.
const HEADER_ALIASES = {
  username: "username",
  user: "username",
  user_name: "username",
  email: "username",
  emailaddress: "username",
  section: "section",
  sectionname: "section",
  sectionid: "section_id",
  section_id: "section_id",
};

const normalizeHeader = (h) => (h || "")
  .toLowerCase()
  .replace(/\(.*?\)/g, "")
  .replace(/[^a-z0-9]+/g, "")
  .trim();

const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result || "");
  reader.onerror = () => reject(new Error("Could not read the uploaded file."));
  reader.readAsText(file);
});

export const ASSIGN_SECTIONS_TEMPLATE_CSV =
  "username,section\nexample@student.edu,Section A\n";

// Flatten the BE's { errors: { field: message } } shape into a list
// of strings for the modal's bullet list. Unknown shapes fall back
// to the raw body or a generic message.
const extractErrors = (err) => {
  const body = err && err.response && err.response.body;
  if (body && body.errors && typeof body.errors === "object") {
    return Object.keys(body.errors).map(k => String(body.errors[k]));
  }
  if (body && body.detail) return [String(body.detail)];
  if (err && err.message) return [err.message];
  return ["Upload failed. Please try again."];
};

export const runAssignSectionsUpload = async ({
  file,
  courseId,
  gameId,
  onProgress,
}) => {
  const report = (stage, extra) => {
    if (onProgress) onProgress({ stage, ...(extra || {}) });
  };

  report("parsing");
  const text = await readFileAsText(file);
  const raw = String(text).replace(/^﻿/, "").split(/\r?\n/);
  const nonEmptyLines = raw.filter(l => l && l.trim().length > 0);

  if (nonEmptyLines.length === 0) {
    const err = new Error("CSV is empty.");
    err.kind = "validation";
    err.errors = ["The uploaded file is empty."];
    throw err;
  }
  if (nonEmptyLines.length < 2) {
    const err = new Error("CSV has no data rows.");
    err.kind = "validation";
    err.errors = ["The uploaded file has no data rows below the header."];
    throw err;
  }

  const headerLine = nonEmptyLines[0];
  const rawHeaders = headerLine.split(",").map(s => s.trim());
  const canonical = rawHeaders.map(h => HEADER_ALIASES[normalizeHeader(h)] || h);

  if (!canonical.includes("username")) {
    const err = new Error("Missing username column.");
    err.kind = "validation";
    err.errors = ['CSV must include a "username" column.'];
    throw err;
  }
  if (!canonical.includes("section") && !canonical.includes("section_id")) {
    const err = new Error("Missing section column.");
    err.kind = "validation";
    err.errors = ['CSV must include a "section" or "section_id" column.'];
    throw err;
  }

  const rewrittenHeader = canonical.join(",");
  const dataRows = nonEmptyLines.slice(1);
  const csv = [rewrittenHeader, ...dataRows].join("\n") + "\n";

  report("uploading");
  const token = Auth.getToken();
  const req = request
    .post(`${API}v2/games/bulk-assign-sections/`)
    .set("Accept", "application/json")
    .attach("file", new Blob([csv], { type: "text/csv" }), "assign-sections.csv")
    .field("course_id", String(courseId))
    .field("game_id", String(gameId));
  if (token) req.set("Authorization", `Token ${token}`);

  let res;
  try {
    res = await req;
  } catch (err) {
    const apiErr = new Error("Upload failed.");
    apiErr.kind = "api";
    apiErr.errors = extractErrors(err);
    throw apiErr;
  }

  const body = (res && res.body) || {};
  report("success", body);
  return body;
};



// WEBPACK FOOTER //
// ./src/instructor/utils/assign-sections-upload.js